import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Deallocations from 'src/pages/people';
import * as deallocationApi from 'src/apis/deallocation.js';
import * as needsApi from 'src/apis/need';
import * as filtersApi from 'src/apis/deallocationFilters';
import * as etlApi from "src/apis/etl";
import * as fitApi from 'src/apis/fit';
import * as positionMapsApi from "src/apis/positionMaps";
import * as responsibleApi from "src/apis/responsible";
import * as commentApi from 'src/apis/comment';
import { useTranslation } from "react-i18next";
import { notification } from 'antd';

/**
* PeopleContainer component - People service layer to call API functions and send fetched data to main page component.
* @returns {JSX.Element}
* example
* <PeopleContainer />
*/
function PeopleContainer() {
  const [listFetching, setListFetching] = useState(false);
  const [deallocations, setDeallocations] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [responsibles] = useState([]);
  const [filters, setFilters] = useState([]);
  const [etl, setEtl] = useState("");
  const [positionMaps, setPositionMaps] = useState();
  const [profileOptions, setProfileOptions] = useState();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });
  const { t } = useTranslation();
  const { sessionUser = {} } = useSelector((state) => state.login);

  /**
  * Get current ETL update datetime.
  */
  const getEtlDate = async () => {
    try {
      const response = await etlApi.getETL();
      const { data } = response;
      const { result } = data;
      if (result.updateTimetable) {
        setEtl(result.updateTimetable);
      }
    } catch (err) {
      setEtl("");
      notification.error({message: t("errors.deallocations.getData")});
    }
  };

  /**
  * Converts an array of values to an array of objects with 'value' and 'label' properties.
  * @param {Array} array - The array of values to convert.
  * @returns {Array} The converted array of objects with 'value' and 'label' properties.
  */
  const arrayToObjectArray = useCallback((array) => {
    const objectArray = array.map(a => a !== 'ADM' ? ({value: a, label: a}) : ({value: '', label: t('all')}));
    return objectArray
  }, [t]);

  /**
  * Get existing responsible profiles
  */
  const getResponsibleProfiles = useCallback(async () => {
    const result = await responsibleApi.getResponsibleProfiles();
    const options = arrayToObjectArray(result.data.result);
    setProfileOptions(options);
  }, [arrayToObjectArray]);

  /**
  * Get all existing active needs.
  */
  const getNeeds = useCallback(async () => {
    try {
      const response = await needsApi.list();
      const { data } = response;
      const { result } = data;
      setNeeds(result);
    } catch(e) {
      notification.error({message: t("errors.deallocations.getData")});
    }
  }, [t]);

  /**
  * Get all existing Position Maps from people.
  */
  const getPositionMaps = async () => {
    try {
      const allPositionMaps = await positionMapsApi.getPositionMaps();
      const formattedPositionMaps = allPositionMaps.data.result.map((item) => ({
        value: item,
        label: item,
      }));
      setPositionMaps(formattedPositionMaps);
    } catch (e) {
      setPositionMaps([]);
      notification.error({message: t("errors.deallocations.getData")});
    }
  };

  /**
  * Get all open and matched deallocations.
  * - Add attribute to split deallocations between Free, Fit and Match.
  */
  const filterDeallocations = useCallback(
    async (filters = {}, paginationOptions = {}) => {
      try {
        setListFetching(true);
        
        const options = {
          limit: paginationOptions.limit || pagination.limit,
          currentPage: paginationOptions.currentPage || pagination.currentPage
        };
        
      
        const response = await deallocationApi.list(filters, options);
      
        
        const { data } = response;
        
        if (data.result && data.result.pagination) {
          setPagination(data.result.pagination);
        }
        
        const result = data.result || data;
        
        const matched = (result.matchDeallocations || []).map((deallocation) => ({
          ...deallocation,
          match: true,
          statusFit: "Match",
        }));
        
        const opened = (result.openedDeallocations || []).map((deallocation) => {
          if (deallocation.needs.length === 0)
            return { ...deallocation, statusFit: t("free") };
          return { ...deallocation, statusFit: "Fit" };
        });
        
        const _deallocations = [...opened, ...matched];
        setDeallocations(_deallocations);
        setListFetching(false);
        
        return Promise.resolve();
      } catch (err) {
        console.error('Error fetching deallocations:', err);
        setListFetching(false);
        notification.error({message: t("errors.deallocations.getData")});
        
        return Promise.reject(err);
      }
    },
    [t, pagination]
  );

  /**
  * Fetch all helper data and handle returned Promises.
  */
  const getHelperData = useCallback(
    async function () {
      try {
        const result = await Promise.all([
          filtersApi.list(),
          getPositionMaps(),
          getNeeds(),
          getEtlDate(),
          getResponsibleProfiles(),
        ]);
        setFilters(result[0].data.result);
      } catch(e) {
        notification.error({message: t("errors.deallocations.getData")});
      }
    },
    [t, getNeeds, getResponsibleProfiles]
  );

  useEffect(() => {
    /**
    * Fetch helper data upon changes and refreshing.
    */
    async function fetchInitData() {
      await Promise.all([getHelperData()]);
      // Initial load of deallocations with default pagination
      filterDeallocations({}, { currentPage: 1, limit: 10 });
    }
    fetchInitData();
  }, [getHelperData]);

  /**
  * Call API method for creating deallocation.
  * @param {Object} deallocation - Deallocation Object
  * @returns {Promise<Object>}
  */
  const handleCreateDeallocation = (deallocation) => {
    return deallocationApi.create(deallocation);
  };

  /**
  * Call API method for updating deallocation.
  * @param {Object} deallocation - Deallocation Object
  * @returns {Promise<Object>}
  */
  const handleUpdateDeallocation = (deallocation) => {
    return deallocationApi.update(deallocation);
  };

  /**
  * Call API method for deleting deallocation.
  * @param {string} deallocationId - Text for Deallocation Id
  * @returns {Promise<Object>}
  */
  const handleDeleteDeallocation = (deallocationId) => {
    return deallocationApi.remove(deallocationId);
  };

  /**
  * Call API method for creating fit.
  * @param {Array<string>} deallocationsIds - Array with deallocation Id to create a fit (only one)
  * @param {Array<string>} needId - Array with needId to create a fit (only one)
  * @returns {Promise<Object>}
  */
  const handleCreateFit = (deallocationIds, needsId) => {
    return fitApi.requestFit(deallocationIds, needsId);
  };

  /**
  * Call API method for canceling fit.
  * @param {string} fitId - Text with fitId to be canceled
  * @returns {Promise<Object>}
  */
  const handleCancelFit = (fitId, cancelCategory, cancelSubcategory) => {
    return fitApi.cancelFit(fitId, cancelCategory, cancelSubcategory);
  };

  /**
  * Call API method for creating a match on a fit.
  * @param {Object} fitMatch - Object with necessary attributes to create a match
  * @returns {Promise<Object>}
  */
  const handleCreateMatch = (fitId, startDate, allocationType) => {
    return fitApi.fitMatchPut({ id: fitId, match: "Y", startDate, allocationType });
  };

  /**
  * Call API method for canceling a match.
  * @param {string} deallocationId - Text with deallocationId.
  * @param {string} needId - Text with needId.
  * @param {string} [description] - Text with optional description.
  * @returns {Promise<Object>}
  */
  const handleRollbackMatch = (deallocationId, needId, description, rollbackCategory, rollbackSubcategory) => {
    return fitApi.rollbackMatch({
      deallocationId,
      needId,
      match: "N",
      active: "N",
      rollbackDescription: description,
      rollbackCategory,
      rollbackSubcategory,
    });
  };

  /**
  * Call API method for getting all comments of a deallocation.
  * @param {string} deallocationId - Text with deallocationId.
  * @returns {Promise<Object>}
  */
  const handleGetComments = (deallocationId) => {
    return commentApi.get(deallocationId);
  }

  /**
  * Call API method for creating a comment
  * @param {Object} comment - Comment object.
  * @returns {Promise<Object>}
  */
  const handleCreateComment = (comment) => {
    return commentApi.add(comment);
  }

  /**
  * Call API method for editing a comment.
  * @param {Object} comment - Comment object
  */
  const handleEditComment = (comment) => {
    return commentApi.edit(comment);
  }

  /**
  * Call API method for deleting a comment (soft delete).
  * @param {string} commentId - Comment id
  */
  const handleDeleteComment = (commentId) => {
    return commentApi.remove(commentId);
  }

  return (
    <Deallocations
      getNeeds={getNeeds}
      getHelperData={getHelperData}
      onCreateDeallocation={handleCreateDeallocation}
      onUpdateDeallocation={handleUpdateDeallocation}
      onDeleteDeallocation={handleDeleteDeallocation}
      onCreateFit={handleCreateFit}
      onCancelFit={handleCancelFit}
      onCreateMatch={handleCreateMatch}
      onCancelMatch={handleRollbackMatch}
      filterDeallocations={filterDeallocations}
      etlDate={etl}
      positionMaps={positionMaps}
      filtersFromApi={filters}
      user={sessionUser}
      deallocations={deallocations}
      listFetching={listFetching}
      needs={needs}
      responsibles={responsibles}
      profileOptions={profileOptions}
      onCreateComment={handleCreateComment}
      onGetComments={handleGetComments}
      onEditComment={handleEditComment}
      onDeleteComment={handleDeleteComment}
      pagination={pagination}
      totalCount={pagination.totalCount}
    />
  );
}

export default PeopleContainer;